import Keys from 'preferences/preferences';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { symptomTrackingActions } from 'store/symptomTracking';
import { selectIsPeriodReminderEnabled, selectReminderTime } from 'store/symptomTracking/selectors';
import { ReminderTime } from 'store/symptomTracking/types';
import usePreferences from './usePreferences';

const useReminderState = () => {
  const dispatch = useDispatch();

  // Redux selectors
  const reminderTime = useSelector(selectReminderTime);
  const isPeriodReminderEnabled = useSelector(selectIsPeriodReminderEnabled);

  // Preference hooks
  const {
    set: setReminderTimePreference,
    clear: clearReminderTimePreference,
    loading: loadingReminderTime,
  } = usePreferences(Keys.TRACKING_REMINDER_TIME, value => {
    if (value.hour !== undefined) {
      const reminderTimeObject: ReminderTime = {
        hour: value.hour,
        minute: value.minute || 0,
      };
      dispatch(symptomTrackingActions.setReminderTime(reminderTimeObject));
    } else {
      dispatch(symptomTrackingActions.setReminderTime(null));
    }
  });

  const {
    set: setPeriodReminderPreference,
    clear: clearPeriodReminderPreference,
    loading: loadingPeriodReminder,
  } = usePreferences(Keys.TRACKING_ENABLE_PERIOD_REMINDER, value => {
    dispatch(symptomTrackingActions.setPeriodReminderEnabled(value === true));
  });

  // Actions that update both preferences and store
  const setReminderTime = useCallback(
    (time: ReminderTime) => {
      setReminderTimePreference(JSON.stringify(time));
      dispatch(symptomTrackingActions.setReminderTime(time));
    },
    [setReminderTimePreference],
  );

  const clearReminderTime = useCallback(() => {
    clearReminderTimePreference();
    dispatch(symptomTrackingActions.setReminderTime(null));
  }, [clearReminderTimePreference]);

  const setPeriodReminderEnabled = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        setPeriodReminderPreference('true');
      } else {
        clearPeriodReminderPreference();
      }
      dispatch(symptomTrackingActions.setPeriodReminderEnabled(enabled));
    },
    [setPeriodReminderPreference, clearPeriodReminderPreference],
  );

  const clearPeriodReminderEnabled = useCallback(() => {
    clearPeriodReminderPreference();
    dispatch(symptomTrackingActions.setPeriodReminderEnabled(false));
  }, [clearPeriodReminderPreference]);

  return {
    // State
    reminderTime,
    isPeriodReminderEnabled,
    areDailyNotificationsEnabled: reminderTime !== null,

    // Loading states
    loadingReminderTime,
    loadingPeriodReminder,

    // Actions
    setReminderTime,
    clearReminderTime,
    setPeriodReminderEnabled,
    clearPeriodReminderEnabled,
  };
};

export default useReminderState;
